import { useState, useEffect, useCallback, useContext, useRef } from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import B2BClientsTable from './B2BClientsTable';
import AddB2BClientModal from './AddB2BClientModal';
import EditB2BClientModal from './EditB2BClientModal';
import ViewB2BClientModal from './ViewB2BClientModal';
import DeleteB2BClientModal from './DeleteB2BClientModal';
import PageHeader from '../../../components/PageHeader/PageHeader';
import { useTranslation } from 'react-i18next';
import { B2BClientService } from '../../../services/B2BServices';
import SnackbarUtils from '../../../utils/SnackbarUtils';
import PluginsContext from '../../../context/PluginsContext';

const B2BClients = () => {
  const { t } = useTranslation();
  const { hasPlugin } = useContext(PluginsContext);

  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [sortModel, setSortModel] = useState([]);
  const [filterModel, setFilterModel] = useState({ items: [] });
  const [searchTerm, setSearchTerm] = useState('');
  const [balanceFilter, setBalanceFilter] = useState('all');
  const [totalRows, setTotalRows] = useState(0);
  const [localInvitationUrl, setLocalInvitationUrl] = useState('');
  const searchTimeoutRef = useRef(null);

  const isLocalDashboard = ['localhost', '127.0.0.1'].includes(window.location.hostname);
  const portalEnabled = hasPlugin('b2b_portal');

  const fetchClients = useCallback(async (
    page,
    pageSize,
    search,
    currentSortModel = sortModel,
    currentFilterModel = filterModel,
    currentBalanceFilter = balanceFilter
  ) => {
    try {
      setLoading(true);
      const activeSort = Array.isArray(currentSortModel) ? currentSortModel[0] : null;
      const activeFilter = currentFilterModel?.items?.find((item) => item?.field && item?.operator);
      const response = await B2BClientService.getClients(page, pageSize, search, {
        sortField: activeSort?.field,
        sortDirection: activeSort?.sort,
        filterField: activeFilter?.field,
        filterOperator: activeFilter?.operator,
        filterValue: activeFilter?.value,
        balanceFilter: currentBalanceFilter
      });
      setClients(response?.clients || []);
      setTotalRows(response?.totalCount || 0);
    } catch (error) {
      console.error('Error fetching B2B clients:', error);
      setClients([]);
      setTotalRows(0);
    } finally {
      setLoading(false);
    }
  }, [sortModel, filterModel, balanceFilter]);

  useEffect(() => {
    fetchClients(
      paginationModel.page + 1,
      paginationModel.pageSize,
      searchTerm,
      sortModel,
      filterModel,
      balanceFilter
    );

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [fetchClients, paginationModel.page, paginationModel.pageSize, sortModel, filterModel, balanceFilter]);

  const handlePaginationChange = (newModel) => {
    setPaginationModel(newModel);
  };

  const handleSortModelChange = (newSortModel) => {
    setSortModel(newSortModel);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const handleFilterModelChange = (newFilterModel) => {
    const normalizedItems = (newFilterModel?.items || [])
      .filter((item) => item?.field && item?.operator)
      .slice(0, 1);

    setFilterModel({ ...newFilterModel, items: normalizedItems });
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const handleSearchChange = useCallback((newSearchTerm) => {
    setSearchTerm(newSearchTerm);
    const currentPageSize = paginationModel.pageSize;
    setPaginationModel((prev) => ({ ...prev, page: 0 }));

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      fetchClients(1, currentPageSize, newSearchTerm, sortModel, filterModel, balanceFilter);
    }, 500);
  }, [paginationModel.pageSize, sortModel, filterModel, balanceFilter, fetchClients]);

  const handleBalanceFilterChange = (newBalanceFilter) => {
    setBalanceFilter(newBalanceFilter);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const handleAddClient = async (newClient) => {
    try {
      await B2BClientService.createClient(newClient);
      await fetchClients(paginationModel.page + 1, paginationModel.pageSize, searchTerm, sortModel, filterModel, balanceFilter);
    } catch (error) {
      console.error('Error creating B2B client:', error);
      throw error; // Re-lanzar para que el modal pueda manejar el error
    }
  };
  
  const handleEditClient = async (updatedClient) => {
    try {
      await B2BClientService.updateClient(updatedClient.id, updatedClient);
      await fetchClients(paginationModel.page + 1, paginationModel.pageSize, searchTerm, sortModel, filterModel, balanceFilter);
    } catch (error) {
      console.error('Error updating B2B client:', error);
      throw error; // Re-lanzar para que el modal pueda manejar el error
    }
  };  

  const handleDeleteClient = async () => {
    try {
      await B2BClientService.deleteClient(selectedClient.id);
      await fetchClients(paginationModel.page + 1, paginationModel.pageSize, searchTerm, sortModel, filterModel, balanceFilter);
      setIsDeleteModalOpen(false);
    } catch (error) {
      console.error('Error deleting B2B client:', error);
      throw error; // Re-lanzar para que el modal pueda manejar el error
    }
  };  

  const handleSendPortalAccess = async (client) => {
    if (!portalEnabled) return;
    if (!client?.email) {
      SnackbarUtils.error('El cliente B2B no tiene email configurado');
      return;
    }

    const confirmed = window.confirm(`Enviar acceso al portal B2B a ${client.email}?`);
    if (!confirmed) return;

    try {
      const invitation = await B2BClientService.sendPortalInvitation(client.id, {
        email: client.email,
        ...(isLocalDashboard
          ? { portalBaseUrl: `${window.location.protocol}//${window.location.hostname}:8081/b2b` }
          : {})
      });

      if (isLocalDashboard && invitation?.invitationUrl) {
        setLocalInvitationUrl(invitation.invitationUrl);
        SnackbarUtils.success('Invitación local generada');
      } else {
        SnackbarUtils.success('Invitación B2B enviada');
      }
    } catch (error) {
      console.error('Error sending B2B portal access:', error);
      SnackbarUtils.error('No se pudo enviar la invitación B2B');
    }
  };

  const handleCopyLocalInvitationUrl = async () => {
    try {
      await navigator.clipboard.writeText(localInvitationUrl);
      SnackbarUtils.success('Enlace copiado');
    } catch (error) {
      console.error('Error copying local B2B invitation URL:', error);
      SnackbarUtils.error('No se pudo copiar el enlace');
    }
  };

  return (
    <Box p={3}>
      <PageHeader title={t('b2b_clients')} subtitle={t('manage_b2b_clients')} />
      <B2BClientsTable
        loading={loading}
        clients={clients}
        paginationModel={paginationModel}
        onPaginationModelChange={handlePaginationChange}
        sortModel={sortModel}
        onSortModelChange={handleSortModelChange}
        filterModel={filterModel}
        onFilterModelChange={handleFilterModelChange}
        searchTerm={searchTerm}
        onSearchChange={handleSearchChange}
        balanceFilter={balanceFilter}
        onBalanceFilterChange={handleBalanceFilterChange}
        totalRows={totalRows}
        onRefresh={() => fetchClients(paginationModel.page + 1, paginationModel.pageSize, searchTerm, sortModel, filterModel, balanceFilter)}
        onAdd={() => setIsAddModalOpen(true)}
        onEdit={(client) => {
          setSelectedClient(client);
          setIsEditModalOpen(true);
        }}
        onView={(client) => {
          setSelectedClient(client);
          setIsViewModalOpen(true);
        }}
        onDelete={(client) => {
          setSelectedClient(client);
          setIsDeleteModalOpen(true);
        }}
        onSendPortalAccess={handleSendPortalAccess}
        showPortal={portalEnabled}
      />
      <AddB2BClientModal
        open={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSave={handleAddClient}
      />
      <EditB2BClientModal
        open={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        onSave={handleEditClient}
        client={selectedClient}
      />
      <ViewB2BClientModal
        open={isViewModalOpen}
        onClose={() => setIsViewModalOpen(false)}
        client={selectedClient}
        onSendPortalAccess={handleSendPortalAccess}
        showPortal={portalEnabled}
      />
      <DeleteB2BClientModal
        clientName={selectedClient?.razonSocial}
        open={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDeleteClient}
      />
      <Dialog
        open={Boolean(localInvitationUrl)}
        onClose={() => setLocalInvitationUrl('')}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Enlace de activación local</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Usá este enlace inmediatamente en el sitio local. Si reenviás la invitación, este enlace deja de ser válido.
          </Typography>
          <TextField
            value={localInvitationUrl}
            fullWidth
            multiline
            minRows={3}
            InputProps={{ readOnly: true }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLocalInvitationUrl('')}>Cerrar</Button>
          <Button variant="contained" startIcon={<ContentCopyIcon />} onClick={handleCopyLocalInvitationUrl}>
            Copiar enlace
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default B2BClients;
